import { Box, Divider, Grid, Typography } from '@mui/material';
import { PieChart as MuiPieChart } from '@mui/x-charts';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chartMutedTextOnLight } from '../../../../../../../theme/chart-ui';
import { SortTypes } from '../../../../../types';
import { TableView } from '../../../../tableview/TableView';
import { ChartHeader } from '../common/chart-header/ChartHeader';
import { BarChartLayouts } from '../common/common-chart-types';
import {
  getChartLegendConfig,
  getChartNoDataOverlay,
} from '../common/common-chart-utils/common-chart-configs';
import {
  CHART_CONTENT_HEIGHT,
  defaultChartColors,
} from '../common/common-chart-utils/common-chart-styles';
import { getBarChartTableDataFromChartData } from '../common/common-chart-utils/common-chart-utils';
import { pieChartStyles } from './pie-chart-styles';
import { PieChartProps } from './pie-chart-types';
import { getMuiPieChartDataConfigFromChartData } from './pie-chart-utils';

export const PieChart = (props: PieChartProps) => {
  const {
    fullScreenHandle,
    titleConfig,
    displayConfig,
    chartData,
    chartColors = defaultChartColors,
    pieBenchmarkSubRows,
    pieFooter,
  } = props;
  const { selectedSortOrder = SortTypes.ASC } = displayConfig;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const {
    showLabels = false,
    showTableView = false,
    showLegend = false,
    layout = BarChartLayouts.GROUPED,
  } = displayConfig;
  const chartDataConfig = getMuiPieChartDataConfigFromChartData(chartData, {
    showLabels,
  });
  // Reusing bar chart function here as its the same as pie chart
  const tableData = getBarChartTableDataFromChartData(chartData, {
    layout,
    selectedSortOrder,
  });
  const { series } = chartDataConfig;
  const { t } = useTranslation();

  const modifiedDisplayConfig = {
    ...displayConfig,
    enablePercentage: false,
    enableStack: false,
    enableSortOrder: false,
    enableGridLines: false,
  };
  const [showTools, setShowTools] = useState(false);
  const hasFooterBlock = Boolean(
    (pieBenchmarkSubRows && pieBenchmarkSubRows.length > 0) || pieFooter
  );

  return (
    <Grid
      item
      container
      ref={chartContainerRef}
      direction="column"
      sx={{ height: '100%' }}
      onMouseEnter={() => setShowTools(true)}
      onMouseLeave={() => setShowTools(false)}
    >
      <ChartHeader
        titleConfig={titleConfig}
        showTools={showTools}
        chartRef={chartRef}
        fullScreenHandle={fullScreenHandle}
        displayConfig={modifiedDisplayConfig}
      />
      <Grid
        item
        container
        direction="column"
        sx={
          hasFooterBlock
            ? {
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }
            : { height: CHART_CONTENT_HEIGHT }
        }
      >
        <Grid
          item
          sx={
            hasFooterBlock
              ? {
                  flex: '1 1 auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }
              : { height: '100%' }
          }
        >
          {showTableView ? (
            <TableView
              tableData={tableData}
              fullScreenHandle={fullScreenHandle}
              chartHeight={
                hasFooterBlock ? ('100%' as const) : CHART_CONTENT_HEIGHT
              }
            />
          ) : (
            <Box
              ref={chartRef}
              sx={{
                width: '100%',
                height: '100%',
                flex: hasFooterBlock ? 1 : undefined,
                minHeight: 0,
              }}
            >
              <MuiPieChart
                sx={pieChartStyles()}
                title={titleConfig.mainTitle}
                series={series}
                margin={{
                  right: 0,
                  left: 0,
                  top: 10,
                  bottom: 10,
                }}
                slotProps={{
                  popper: { container: () => fullScreenHandle.node.current },
                  legend: getChartLegendConfig(showLegend),
                  noDataOverlay: getChartNoDataOverlay(t),
                }}
                colors={chartColors}
              />
            </Box>
          )}
        </Grid>
        {pieBenchmarkSubRows && pieBenchmarkSubRows.length > 0 ? (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Grid item sx={{ flexShrink: 0, width: '100%', px: 0.5, pb: 0.5 }}>
              {pieBenchmarkSubRows.map((row, i) => (
                <Box
                  key={`${row.title}-${i}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: chartMutedTextOnLight,
                      flex: '1 1 auto',
                      minWidth: 0,
                    }}
                  >
                    {row.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ flexShrink: 0, fontWeight: 500 }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Grid>
          </>
        ) : null}
        {pieFooter ? (
          <Typography
            variant="caption"
            sx={{
              color: chartMutedTextOnLight,
              display: 'block',
              flexShrink: 0,
              px: 0.5,
              pb: 0.25,
              pt: 0.25,
            }}
          >
            {pieFooter}
          </Typography>
        ) : null}
      </Grid>
    </Grid>
  );
};
